/* -------------------------------------------------------------------------- */
/*                              Amount Helpers                                */
/* -------------------------------------------------------------------------- */

export const toAmount = (
  value,
  fallback = 0
) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  const cleanedValue =
    typeof value === "string"
      ? value
          .replace(/₹/g, "")
          .replace(/,/g, "")
          .trim()
      : value;

  const amount = Number(cleanedValue);

  return Number.isFinite(amount)
    ? amount
    : fallback;
};

export const roundMoney = (value) =>
  Math.round(
    (toAmount(value) +
      Number.EPSILON) *
      100
  ) / 100;

export const formatMoney = (
  value,
  options = {}
) => {
  const {
    showSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  const formatted = toAmount(
    value
  ).toLocaleString("en-IN", {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showSymbol
    ? `₹${formatted}`
    : formatted;
};

export const clampAmount = (
  value,
  minimum = 0,
  maximum = Number.MAX_SAFE_INTEGER
) =>
  Math.min(
    maximum,
    Math.max(
      minimum,
      toAmount(value)
    )
  );

/* -------------------------------------------------------------------------- */
/*                             Array Total Helper                             */
/* -------------------------------------------------------------------------- */

export const calculateItemsTotal = (
  items = []
) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  return roundMoney(
    items.reduce(
      (total, item) => {
        const savedAmount =
          toAmount(item?.amount);

        if (savedAmount > 0) {
          return (
            total + savedAmount
          );
        }

        const quantity = Math.max(
          0,
          toAmount(
            item?.quantity,
            1
          )
        );

        const rate = Math.max(
          0,
          toAmount(item?.rate)
        );

        return (
          total +
          quantity * rate
        );
      },
      0
    )
  );
};

/* -------------------------------------------------------------------------- */
/*                         Payment Total Calculation                          */
/* -------------------------------------------------------------------------- */

export const calculatePaidTotal = (
  stay = {}
) => {
  if (
    stay?.paidTotal !== undefined &&
    stay?.paidTotal !== null
  ) {
    return roundMoney(
      stay.paidTotal
    );
  }

  if (stay?.paid !== undefined && stay?.paid !== null) return roundMoney(stay.paid);

  const advance = Math.max(
    0,
    toAmount(stay?.advance)
  );

  const payments =
    Array.isArray(stay?.payments)
      ? stay.payments
      : [];

  const paymentTotal =
    calculateItemsTotal(payments);

  const advanceAlreadyInPayments =
    payments.some(
      (payment) =>
        payment?.source ===
          "check_in_advance" ||
        payment?.source ===
          "check_in_source_prepaid" ||
        payment?.source ===
          "check_in_guest_payment" || payment?.source === "booking_advance_applied" || payment?.source === "check_in_prior_advance" ||
        payment?.note ===
          "Check-In Advance" ||
        payment?.title ===
          "Advance Received"
    );

  return roundMoney(
    advanceAlreadyInPayments
      ? paymentTotal
      : advance + paymentTotal
  );
};

/* -------------------------------------------------------------------------- */
/*                          Discount Calculation                              */
/* -------------------------------------------------------------------------- */

export const calculateDiscount = ({
  grossTotal,
  discountType = "flat",
  discountValue = 0,
  discountAmount,
}) => {
  const gross = Math.max(
    0,
    toAmount(grossTotal)
  );

  if (
    discountType !== "percent" &&
    discountAmount !== undefined &&
    discountAmount !== null
  ) {
    return roundMoney(
      Math.min(
        gross,
        Math.max(
          0,
          toAmount(
            discountAmount
          )
        )
      )
    );
  }

  const value = Math.max(
    0,
    toAmount(discountValue)
  );

  if (
    discountType === "percent"
  ) {
    const percentage =
      Math.min(100, value);

    return roundMoney(
      Math.min(
        gross,
        (gross * percentage) /
          100
      )
    );
  }

  return roundMoney(
    Math.min(gross, value)
  );
};

/* -------------------------------------------------------------------------- */
/*                              GST Calculation                               */
/* -------------------------------------------------------------------------- */

export const calculateGst = ({
  taxableAmount,
  gstRate,
}) => {
  const taxable = Math.max(
    0,
    toAmount(taxableAmount)
  );

  const rate = Math.max(
    0,
    toAmount(gstRate)
  );

  const gstAmount = roundMoney(
    (taxable * rate) / 100
  );

  const cgst = roundMoney(
    gstAmount / 2
  );

  const sgst = roundMoney(
    gstAmount - cgst
  );

  return {
    gstRate: rate,
    gstAmount,
    cgst,
    sgst,
  };
};

/* -------------------------------------------------------------------------- */
/*                          Complete Bill Calculation                         */
/* -------------------------------------------------------------------------- */

export const calculateBill = (
  stay = {},
  overrides = {}
) => {
  const roomRent = Math.max(
    0,
    toAmount(
      overrides.roomRent ??
        stay?.tariff ??
        stay?.roomRent ??
        stay?.baseTariff
    )
  );

  const food =
    overrides.food !==
    undefined
      ? Math.max(
          0,
          toAmount(overrides.food)
        )
      : stay?.foodTotal !==
        undefined
      ? Math.max(
          0,
          toAmount(
            stay.foodTotal
          )
        )
      : calculateItemsTotal(
          stay?.foodItems
        );

  const laundry =
    overrides.laundry !==
    undefined
      ? Math.max(
          0,
          toAmount(
            overrides.laundry
          )
        )
      : stay?.laundryTotal !==
        undefined
      ? Math.max(
          0,
          toAmount(
            stay.laundryTotal
          )
        )
      : calculateItemsTotal(
          stay?.laundryItems
        );

  const extra =
    overrides.extra !==
    undefined
      ? Math.max(
          0,
          toAmount(overrides.extra)
        )
      : stay?.extraTotal !==
        undefined
      ? Math.max(
          0,
          toAmount(
            stay.extraTotal
          )
        )
      : calculateItemsTotal(
          stay?.extraItems
        );

  const grossTotal = roundMoney(
    roomRent +
      food +
      laundry +
      extra
  );

  const discountType =
    overrides.discountType ??
    stay?.discountType ??
    "flat";

  const discountValue =
    overrides.discountValue ??
    stay?.discountValue ??
    stay?.discountAmount ??
    0;

  const discount =
    calculateDiscount({
      grossTotal,

      discountType,

      discountValue,

      discountAmount:
        overrides.discountAmount ??
        stay?.discountAmount,
    });

  const taxableAmount =
    roundMoney(
      Math.max(
        0,
        grossTotal - discount
      )
    );

  const gst = calculateGst({
    taxableAmount,

    gstRate:
      overrides.gstRate ??
      stay?.gstRate ??
      0,
  });

  const grandTotal =
    roundMoney(
      taxableAmount +
        gst.gstAmount + Math.max(0, toAmount(overrides.damageCharge ?? stay.damageCharge))
    );

  const allocatedPaid =
    overrides.paid !==
    undefined
      ? roundMoney(
          Math.max(
            0,
            toAmount(
              overrides.paid
            )
          )
        )
      : calculatePaidTotal(stay);

  const advanceCredit = stay.paidAllocationNormalized === true
    ? Math.max(0, toAmount(overrides.advanceCreditBalance ?? stay.advanceCreditBalance ?? stay.excessAdvanceCredit)) : 0;
  const paid = roundMoney(allocatedPaid + advanceCredit);
  const creditApplied = roundMoney(Math.min(advanceCredit, Math.max(0, grandTotal - allocatedPaid)));

  const rawBalance =
    roundMoney(
      grandTotal - paid
    );

  const balance = roundMoney(
    Math.max(
      0,
      rawBalance
    )
  );

  const excessPaid =
    roundMoney(
      Math.max(
        0,
        paid - grandTotal
      )
    );

  return {
    roomRent:
      roundMoney(roomRent),

    food: roundMoney(food),

    laundry:
      roundMoney(laundry),

    extra: roundMoney(extra),

    grossTotal,

    discountType,

    discountValue:
      roundMoney(
        discountValue
      ),

    discount,

    taxableAmount,

    gstRate: gst.gstRate,

    gstAmount:
      gst.gstAmount,

    cgst: gst.cgst,

    sgst: gst.sgst,

    grandTotal,
    damageCharge: roundMoney(Math.max(0, toAmount(overrides.damageCharge ?? stay.damageCharge))),
    allocatedPaid, advanceCredit, creditApplied,

    paid,

    balance,

    excessPaid,

    paymentStatus:
      balance <= 0
        ? "paid"
        : paid > 0
        ? "partial"
        : "pending",
  };
};

/* -------------------------------------------------------------------------- */
/*                         Hotel Number Prefix                                */
/* -------------------------------------------------------------------------- */

const createHotelPrefix = (
  hotelId,
  maximumLength = 6
) => {
  const prefix = String(
    hotelId || "HOTEL"
  )
    .replace(
      /[^a-z0-9]/gi,
      ""
    )
    .slice(0, maximumLength)
    .toUpperCase();

  return prefix || "HOTEL";
};

const createDatePart = (
  date = new Date()
) =>
  [
    date.getFullYear(),

    String(
      date.getMonth() + 1
    ).padStart(2, "0"),

    String(
      date.getDate()
    ).padStart(2, "0"),
  ].join("");

const createTimePart = (
  date = new Date()
) =>
  [
    String(
      date.getHours()
    ).padStart(2, "0"),

    String(
      date.getMinutes()
    ).padStart(2, "0"),

    String(
      date.getSeconds()
    ).padStart(2, "0"),
  ].join("");

const createRandomPart = () =>
  Math.random()
    .toString(36)
    .slice(2, 6)
    .toUpperCase();

/* -------------------------------------------------------------------------- */
/*                           Invoice Number                                   */
/* -------------------------------------------------------------------------- */

export const createInvoiceNumber = (
  hotelId = "HOTEL",
  date = new Date()
) => {
  const validDate =
    date instanceof Date &&
    !Number.isNaN(
      date.getTime()
    )
      ? date
      : new Date();

  return [
    "INV",

    createHotelPrefix(
      hotelId,
      6
    ),

    createDatePart(
      validDate
    ),

    createTimePart(
      validDate
    ),

    createRandomPart(),
  ].join("-");
};

/* -------------------------------------------------------------------------- */
/*                           Receipt Number                                   */
/* -------------------------------------------------------------------------- */

export const createReceiptNumber = (
  hotelId = "HOTEL",
  date = new Date()
) => {
  const validDate =
    date instanceof Date &&
    !Number.isNaN(
      date.getTime()
    )
      ? date
      : new Date();

  return [
    "RCPT",

    createHotelPrefix(
      hotelId,
      5
    ),

    createDatePart(
      validDate
    ),

    createTimePart(
      validDate
    ),

    createRandomPart(),
  ].join("-");
};

/* -------------------------------------------------------------------------- */
/*                          Credit Note Number                                */
/* -------------------------------------------------------------------------- */

export const createCreditNoteNumber = (
  hotelId = "HOTEL",
  date = new Date()
) => {
  const validDate =
    date instanceof Date &&
    !Number.isNaN(
      date.getTime()
    )
      ? date
      : new Date();

  return [
    "CN",

    createHotelPrefix(
      hotelId,
      5
    ),

    createDatePart(
      validDate
    ),

    createTimePart(
      validDate
    ),

    createRandomPart(),
  ].join("-");
};

/* -------------------------------------------------------------------------- */
/*                         Normalize Payment Entry                            */
/* -------------------------------------------------------------------------- */

export const normalizePaymentEntry = (
  payment = {},
  options = {}
) => {
  const {
    hotelId = "HOTEL",

    generateReceipt = true,
  } = options;

  const amount = roundMoney(
    Math.max(
      0,
      toAmount(payment.amount)
    )
  );

  return {
    ...payment,

    amount,

    quantity: 1,

    rate: amount,

    paymentMode:
      payment.paymentMode ||
      "Cash",

    transactionReference:
      String(
        payment.transactionReference ||
          ""
      ).trim(),

    note: String(
      payment.note || ""
    ).trim(),

    title:
      payment.title ||
      "Payment Received",

    source:
      payment.source ||
      "room_payment",

    addedAt:
      payment.addedAt ||
      new Date().toISOString(),

    receiptNumber:
      payment.receiptNumber ||
      (generateReceipt
        ? createReceiptNumber(
            hotelId
          )
        : ""),
  };
};

/* -------------------------------------------------------------------------- */
/*                           Payment Summary                                  */
/* -------------------------------------------------------------------------- */

export const getPaymentModeSummary = (
  payments = []
) => {
  const summary = {
    Cash: 0,
    UPI: 0,
    Card: 0,
    "Bank Transfer": 0,
    OTA: 0,
    Other: 0,
    total: 0,
  };

  if (!Array.isArray(payments)) {
    return summary;
  }

  payments.forEach((payment) => {
    const amount = Math.max(
      0,
      toAmount(payment?.amount)
    );

    const mode =
      payment?.paymentMode;

    if (
      Object.prototype.hasOwnProperty.call(
        summary,
        mode
      )
    ) {
      summary[mode] =
        roundMoney(
          summary[mode] +
            amount
        );
    } else {
      summary.Other =
        roundMoney(
          summary.Other +
            amount
        );
    }

    summary.total =
      roundMoney(
        summary.total +
          amount
      );
  });

  return summary;
};

/* -------------------------------------------------------------------------- */
/*                     Stay Balance After Manual Payment                       */
/* -------------------------------------------------------------------------- */

export const calculateStayBalanceAfterPayment = (
  stay = {},
  paidTotalOverride = 0
) => {
  const paidTotal =
    roundMoney(
      Math.max(
        0,
        toAmount(
          paidTotalOverride
        )
      )
    );

  const bill =
    calculateBill(
      stay,
      {
        paid: paidTotal,
      }
    );

  return {
    paidTotal,

    balance:
      bill.balance,

    grandTotal:
      bill.grandTotal,

    paymentStatus:
      bill.paymentStatus,

    excessPaid:
      bill.excessPaid,
  };
};

/* -------------------------------------------------------------------------- */
/*                               Default Export                               */
/* -------------------------------------------------------------------------- */

const billingService = {
  calculateBill,
  calculateStayBalanceAfterPayment,
  calculateDiscount,
  calculateGst,
  calculateItemsTotal,
  calculatePaidTotal,
  clampAmount,
  createCreditNoteNumber,
  createInvoiceNumber,
  createReceiptNumber,
  formatMoney,
  getPaymentModeSummary,
  normalizePaymentEntry,
  roundMoney,
  toAmount,
};

export default billingService;
// Persist the SAME bill model used by previews and invoices. paidTotal remains allocated;
// unspent advance remains a liability, and must never be counted twice when CNU consumes it.
export const financialFields = (stay = {}) => {
  const bill = calculateBill(stay);
  return {grossTotal: bill.grossTotal, discountAmount: bill.discount, taxableAmount: bill.taxableAmount,
    gstAmount: bill.gstAmount, cgst: bill.cgst, sgst: bill.sgst, grandTotal: bill.grandTotal,
    balance: bill.balance, excessPayment: bill.excessPaid, paymentStatus: bill.paymentStatus};
};

export const calculateFoodPending = (stay = {}) => roundMoney(Math.min(calculateBill(stay).balance, Math.max(0, calculateBill(stay).food - toAmount(stay.foodPaidTotal))));
